import PropTypes from 'prop-types';
import _get from 'lodash/get';
import clsx from 'clsx';
import React, { useState } from 'react';
import { AccordionPanel, Button, CheckboxGroup, Collapsible, Glyphicon, Text, SubHeading } from 'reaxl';
import FilterCheckboxLabel from '../FilterCheckboxLabel';
import FilterTitle from '../FilterTitle';

// Ensure the value used in the component is an array as we assume all FilterCheckboxes are multi-select
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const getValue = (values, name) => [].concat(_get(values, name, []));
const getFilterTitle = (value, label) => {
  const valueCount = value.length;
  let filterTitle = label;
  if (valueCount > 0) {
    filterTitle = /*#__PURE__*/_jsxs(_Fragment, {
      children: [label, ' ', /*#__PURE__*/_jsx(Text, {
        weight: "normal",
        color: "subdued-lighter",
        children: `(${valueCount} selected)`
      })]
    });
  }
  return /*#__PURE__*/_jsx(FilterTitle, {
    title: filterTitle
  }, "filter-title");
};
const updateOptionLabels = options => options && options.map(option => ({
  ...option,
  label: /*#__PURE__*/_jsx(FilterCheckboxLabel, {
    label: option.label,
    count: option.count,
    disabled: option.disabled,
    moreInfo: option.moreInfo,
    filterBadge: option.filterBadge
  })
}));
const filterArray = function (options, optionsSublist, optionsSubIndex) {
  if (optionsSubIndex === void 0) {
    optionsSubIndex = [];
  }
  let filterOptions = options;
  if (optionsSublist) {
    // We must add count label to the options sublist
    // as it is not handled by the initial update
    filterOptions = updateOptionLabels(optionsSublist);
  }
  return filterOptions.reduce((acc, item, index) => {
    if (!optionsSubIndex.length || optionsSubIndex.includes(index)) {
      acc.displayOptions.push(item);
    } else {
      acc.expansionOptions.push(item);
    }
    return acc;
  }, {
    displayOptions: [],
    expansionOptions: []
  });
};
const getSubGroupDisplayOptions = (optionsGroups, options) => {
  let filterSubOptionGroups;
  if (optionsGroups) {
    // filter the options array to only elements that are included in the optionsSubIndex array
    filterSubOptionGroups = optionsGroups.map(optionGroup => {
      let subGroupDisplayOptions = [];
      let expansionGroup = [];
      if (optionGroup.optionsSubIndex) {
        subGroupDisplayOptions = options.filter((option, index) => optionGroup.optionsSubIndex.includes(index));
        const expansionSubArray = optionGroup.expansionGroupSubIndex?.filter(item => !optionGroup.optionsSubIndex.includes(item)) || [];
        expansionGroup = options.filter((option, index) => expansionSubArray.includes(index));
      } else {
        subGroupDisplayOptions = optionGroup.expansionGroupSubIndex && options.filter((option, index) => optionGroup.expansionGroupSubIndex.includes(index));
      }
      return {
        title: optionGroup.title,
        subGroupDisplayOptions,
        expansionGroup
      };
    });
  }
  return filterSubOptionGroups;
};
const CheckboxFilterData = (filter, values) => {
  const {
    label,
    name,
    options: optionsProps,
    optionsGroups,
    optionsSublist,
    optionsSubIndex
  } = filter;
  const value = getValue(values, name);
  const options = updateOptionLabels(optionsProps);
  const {
    displayOptions = {},
    expansionOptions = {}
  } = filterArray(options, optionsSublist, optionsSubIndex) || {};
  const optionsSubGroups = getSubGroupDisplayOptions(optionsGroups, options);
  const hasExpansion = !!optionsSublist || !!optionsSubIndex;
  const title = getFilterTitle(value, label);
  return {
    name,
    options,
    displayOptions,
    expansionOptions,
    optionsSubGroups,
    value,
    hasExpansion,
    title
  };
};
const renderCheckboxGroup = (displayOptions, expansionOptions, componentProps) => {
  const {
    filter,
    onOptionChange,
    value,
    hasExpansion,
    customFilterOptions,
    showMore,
    toggleSeeMore,
    xs,
    sm,
    md,
    lg
  } = componentProps;
  const getFilterOptions = formattedOptions => formattedOptions && formattedOptions.map(option => ({
    ...option,
    label: option.label
  }));
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(CheckboxGroup, {
      options: customFilterOptions && customFilterOptions(displayOptions) || getFilterOptions(displayOptions),
      onChange: event => onOptionChange(event, filter),
      value: value,
      hasVisual: !!customFilterOptions,
      xs: xs,
      sm: sm,
      md: md,
      lg: lg
    }), hasExpansion && /*#__PURE__*/_jsx(Collapsible, {
      className: clsx({
        'display-grid checkbox-group': true,
        row: !!customFilterOptions
      }),
      toggleFooter: /*#__PURE__*/_jsxs(Button, {
        bsStyle: "text",
        "data-cmp": "lnk-expand",
        className: clsx({
          'margin-horizontal-2': !!customFilterOptions
        }),
        children: [showMore ? 'See Less' : 'See More', /*#__PURE__*/_jsx(Glyphicon, {
          glyph: showMore ? 'chevron-up' : 'chevron-down',
          className: "margin-horizontal-2"
        })]
      }),
      initialCollapse: true,
      onToggle: toggleSeeMore,
      children: /*#__PURE__*/_jsx(CheckboxGroup, {
        options: customFilterOptions && customFilterOptions(expansionOptions) || getFilterOptions(expansionOptions),
        onChange: event => onOptionChange(event, filter),
        value: value,
        hasVisual: !!customFilterOptions,
        xs: xs,
        sm: sm,
        md: md,
        lg: lg
      })
    }, "expansionSeeMoreBtn")]
  });
};
const renderCheckboxGroupWithTitle = (_ref, expansionOptions, componentProps) => {
  let {
    title,
    subGroupDisplayOptions,
    expansionGroup
  } = _ref;
  let extendedComponentProps = componentProps;
  if (expansionGroup && expansionGroup.length > 1) {
    extendedComponentProps = {
      ...extendedComponentProps,
      hasExpansion: !!expansionGroup,
      options: expansionGroup
    };
  }
  return /*#__PURE__*/_jsxs("div", {
    className: "margin-bottom-4",
    children: [/*#__PURE__*/_jsx(SubHeading, {
      className: "text-gray margin-bottom-2",
      size: 300,
      children: title
    }), /*#__PURE__*/_jsx("div", {
      className: "margin-left-4",
      children: renderCheckboxGroup(subGroupDisplayOptions, expansionGroup, extendedComponentProps)
    })]
  }, title);
};
const renderFilterCheckboxes = (_ref2, componentProps) => {
  let {
    hasExpansion,
    value,
    displayOptions,
    expansionOptions,
    options,
    optionsSubGroups
  } = _ref2;
  const extendedComponentProps = {
    ...componentProps,
    value,
    options,
    hasExpansion
  };
  if (optionsSubGroups) {
    return optionsSubGroups.map(optionGroup => renderCheckboxGroupWithTitle(optionGroup, expansionOptions, extendedComponentProps));
  }
  return renderCheckboxGroup(displayOptions, expansionOptions, extendedComponentProps);
};

/**
 *  Filter Checkxboxes renders a group of checkbox elements based off of filter option data
 *
 */
function FilterCheckboxes(_ref3) {
  let {
    collapsed,
    collapsedTitle,
    customFilterOptions,
    filter = {},
    onOptionChange = () => {},
    values = {},
    withAccordion = true,
    ...rest
  } = _ref3;
  // Clean the unused props
  delete rest.filters;
  delete rest.filtersOrder;
  delete rest.resultCount;
  delete rest.errors;
  delete rest.onOptionsClear;
  delete rest.onShowExpansion;
  delete rest.onHideExpansion;
  const [showMore, setShowMore] = useState(false);
  const toggleSeeMore = () => {
    setShowMore(!showMore);
  };

  // getting filterCheck
  const filterData = CheckboxFilterData(filter, values);
  const filters = renderFilterCheckboxes(filterData, {
    collapsed,
    collapsedTitle,
    filter,
    onOptionChange,
    customFilterOptions,
    showMore,
    toggleSeeMore,
    ...rest
  });
  return withAccordion ? /*#__PURE__*/_jsx(AccordionPanel, {
    "data-cmp": "filterCheckboxes",
    eventKey: filterData.name,
    title: filterData.title,
    ...rest,
    children: filters
  }) : filters;
}
export default FilterCheckboxes;